<template lang="pug">
v-container(fluid='')
  v-card
    v-card-title.d-flex.justify-space-between
      span Legajos de Personal
      v-btn(color='primary', @click='exportToExcel', v-if="$vuetify.breakpoint.mdAndUp")
        v-icon(left='') mdi-file-export
        | Exportar
    v-card-text
      v-row
        v-col(cols='12')
          v-text-field(
            v-model='search',
            label='Buscar empleado',
            prepend-icon='mdi-magnify',
            dense='',
            outlined='',
            hide-details='',
            clearable
          )
      
      v-data-table.elevation-1.mt-4(
        :headers='headers',
        :items='employees',
        :search='search',
        :loading='loading',
        :items-per-page="10",
        :footer-props="{'items-per-page-options': [5, 10, 15, 25]}")
        template(v-slot:item.userName='{ item }')
          v-chip(
            v-if="!item.hasFile"
            color="warning"
            x-small
            class="mr-2"
          ) Sin legajo
          | {{ item.userName }}
        template(v-slot:item.employmentInfo.entryDate='{ item }')
          | {{ formatDate(item.employmentInfo.entryDate) }}
        template(v-slot:item.probationStatus='{ item }')
          v-chip(
            :color='getProbationStatusColor(item)', 
            small='',
            v-if="item.employmentInfo && item.employmentInfo.entryDate"
          )
            | {{ getProbationStatusText(item) }}
        template(v-slot:item.employmentInfo.contractType="{ item }")
          | {{ getContractTypeLabel(item.employmentInfo?.contractType) }}
        template(v-slot:item.actions='{ item }')
          // Para usuarios sin legajo, mostrar solo el botón de crear legajo
          v-tooltip(bottom, v-if="!item.hasFile")
            template(v-slot:activator="{ on, attrs }")
              v-btn(
                icon='',
                small='',
                @click='createOrEditFile({userId: item.userId})',
                color="success",
                v-bind="attrs"
                v-on="on"
              )
                v-icon mdi-file-plus
            span Crear Legajo
            
          // Para usuarios con legajo, mostrar todas las acciones
          template(v-if="item.hasFile")
            v-tooltip(bottom)
              template(v-slot:activator="{ on, attrs }")
                v-btn.mr-1(
                  icon='',
                  small='',
                  @click='viewEmployeeFile(item)',
                  color="primary",
                  v-bind="attrs"
                  v-on="on"
                )
                  v-icon mdi-eye
              span Ver Legajo
            v-tooltip(bottom)
              template(v-slot:activator="{ on, attrs }")
                v-btn.mr-1(
                  icon='',
                  small='',
                  @click='viewAttendance(item.userId._id)',
                  color="info",
                  v-bind="attrs"
                  v-on="on"
                )
                  v-icon mdi-clock-check
              span Ver Asistencia
            v-tooltip(bottom)
              template(v-slot:activator="{ on, attrs }")
                v-btn.mr-1(
                  icon='',
                  small='',
                  @click='viewSchedule(item.userId._id)',
                  color="success",
                  v-bind="attrs"
                  v-on="on"
                )
                  v-icon mdi-calendar-clock
              span Ver Horarios
            v-tooltip(bottom)
              template(v-slot:activator="{ on, attrs }")
                v-btn(
                  icon='',
                  small='',
                  @click='createOrEditFile(item)',
                  color="warning",
                  v-bind="attrs"
                  v-on="on"
                )
                  v-icon mdi-pencil
              span Editar
      
      // Botón flotante para crear nuevo legajo (visible en móviles)
      v-btn(
        fab,
        color="primary",
        fixed,
        bottom,
        right,
        @click="createNewFile",
        v-if="$vuetify.breakpoint.smAndDown"
      )
        v-icon mdi-plus

  // Diálogo para crear/editar legajo
  v-dialog(v-model="fileDialog", max-width="800", scrollable)
    v-card
      v-card-title
        | {{ editedFile._id ? 'Editar Legajo' : 'Nuevo Legajo' }}
      v-card-text
        v-form(ref="fileForm", v-model="valid")
          h3.mb-4 Información del Empleado
          v-autocomplete(
            v-model="editedFile.userId",
            :items="employeeList",
            item-text="name",
            item-value="_id", 
            label="Empleado",
            :disabled="!!editedFile._id",
            :rules="[v => !!v || 'Debe seleccionar un empleado']",
            required,
            return-object
          )
            template(v-slot:selection="{ item }")
              | {{ item.name }} ({{ item.email }})
            template(v-slot:item="{ item }")
              | {{ item.name }}
              .text-caption {{ item.email }}
          
          h3.mb-4.mt-6 Información Personal
          v-row
            v-col(cols="12", sm="6")
              v-text-field(
                v-model="editedFile.personalInfo.dni",
                label="DNI",
                outlined,
                dense
              )
            v-col(cols="12", sm="6")
              v-text-field(
                v-model="editedFile.personalInfo.cuil",
                label="CUIL",
                outlined,
                dense
              )
          v-row
            v-col(cols="12", sm="6")
              v-menu(
                ref="dateMenu",
                v-model="dateMenu",
                :close-on-content-click="false",
                transition="scale-transition",
                offset-y,
                min-width="auto"
              )
                template(v-slot:activator="{ on, attrs }")
                  v-text-field(
                    v-model="formattedBirthDate",
                    label="Fecha de Nacimiento",
                    readonly,
                    outlined,
                    dense,
                    v-bind="attrs",
                    v-on="on"
                  )
                v-date-picker(
                  v-model="birthDate",
                  @input="dateMenu = false",
                  :max="new Date().toISOString().substr(0, 10)"
                )
            v-col(cols="12", sm="6")
              v-text-field(
                v-model="editedFile.personalInfo.phone",
                label="Teléfono",
                outlined,
                dense
              )
          
          h3.mb-4.mt-6 Dirección
          v-row
            v-col(cols="12", sm="8")
              v-text-field(
                v-model="editedFile.personalInfo.address.street",
                label="Calle",
                outlined,
                dense
              )
            v-col(cols="12", sm="4")
              v-text-field(
                v-model="editedFile.personalInfo.address.number",
                label="Número",
                outlined,
                dense
              )
          v-row
            v-col(cols="12", sm="3")
              v-text-field(
                v-model="editedFile.personalInfo.address.floor",
                label="Piso",
                outlined,
                dense
              )
            v-col(cols="12", sm="3")
              v-text-field(
                v-model="editedFile.personalInfo.address.apartment",
                label="Depto",
                outlined,
                dense
              )
            v-col(cols="12", sm="6")
              v-text-field(
                v-model="editedFile.personalInfo.address.city",
                label="Ciudad",
                outlined,
                dense
              )
          
          h3.mb-4.mt-6 Información Laboral
          v-row
            v-col(cols="12", sm="6")
              v-menu(
                ref="entryDateMenu",
                v-model="entryDateMenu",
                :close-on-content-click="false",
                transition="scale-transition",
                offset-y,
                min-width="auto"
              )
                template(v-slot:activator="{ on, attrs }")
                  v-text-field(
                    v-model="formattedEntryDate",
                    label="Fecha de Ingreso",
                    readonly,
                    outlined,
                    dense,
                    :rules="[v => !!v || 'Fecha de ingreso requerida']",
                    required,
                    v-bind="attrs",
                    v-on="on"
                  )
                v-date-picker(
                  v-model="entryDate",
                  @input="entryDateMenu = false",
                  :max="new Date().toISOString().substr(0, 10)"
                )
            v-col(cols="12", sm="6")
              v-select(
                v-model="editedFile.employmentInfo.contractType",
                :items="contractTypes",
                item-text="text",
                item-value="value",
                label="Tipo de Contrato",
                outlined,
                dense
              )
          v-row
            v-col(cols="12", sm="6")
              v-select(
                v-model="editedFile.employmentInfo.roleId",
                :items="roles",
                item-text="name",
                item-value="_id",
                label="Rol / Puesto",
                outlined,
                dense,
                :rules="[v => !!v || 'Debe seleccionar un rol']",
                required
              )
            v-col(cols="12", sm="6")
              v-text-field(
                v-model="editedFile.employmentInfo.position",
                label="Cargo específico",
                outlined,
                dense
              )
          v-row
            v-col(cols="12", sm="6")
              v-text-field(
                v-model.number="editedFile.employmentInfo.hourlyRate",
                label="Valor hora",
                outlined,
                dense,
                type="number",
                min="0",
                prefix="$"
              )
              .caption.grey--text Si no se especifica, se utilizará la tarifa asociada al rol
            v-col(cols="12", sm="6")
              v-text-field(
                v-model.number="editedFile.employmentInfo.baseSalary",
                label="Sueldo base mensual",
                outlined,
                dense,
                prefix="$"
              )
          
          h3.mb-4.mt-6 Información Bancaria
          v-row
            v-col(cols="12", sm="6")
              v-text-field(
                v-model="editedFile.bankInfo.bank",
                label="Banco",
                outlined,
                dense
              )
            v-col(cols="12", sm="6")
              v-text-field(
                v-model="editedFile.bankInfo.accountType",
                label="Tipo de Cuenta",
                outlined,
                dense
              )
          v-row
            v-col(cols="12", sm="6")
              v-text-field(
                v-model="editedFile.bankInfo.cbu",
                label="CBU",
                outlined,
                dense
              )
            v-col(cols="12", sm="6")
              v-text-field(
                v-model="editedFile.bankInfo.alias",
                label="Alias",
                outlined,
                dense
              )
      
      v-card-actions
        v-spacer
        v-btn(text, @click="fileDialog = false") Cancelar
        v-btn(color="primary", :disabled="!valid", @click="saveFile") Guardar
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

export default {
  name: 'EmployeeFileManager',

  data: () => ({
    search: '',
    loading: false,
    fileDialog: false,
    valid: false,
    dateMenu: false,
    entryDateMenu: false,
    birthDate: null,
    entryDate: null,
    editedFile: {
      userId: null,
      personalInfo: {
        dni: '',
        cuil: '',
        birthDate: null,
        address: {
          street: '',
          number: '',
          floor: '',
          apartment: '',
          city: '',
          postalCode: '',
          province: ''
        },
        phone: '',
        alternativePhone: '',
        emergencyContact: {
          name: '',
          relationship: '',
          phone: ''
        }
      },
      bankInfo: {
        accountType: '',
        bank: '',
        cbu: '',
        alias: ''
      },
      employmentInfo: {
        entryDate: null,
        contractType: 'fullTime',
        roleId: null,
        position: '',
        hourlyRate: null,
        baseSalary: null,
        additionalInfo: ''
      }
    },
    headers: [
      { text: 'Empleado', value: 'userId.name', align: 'start' },
      { text: 'Puesto', value: 'employmentInfo.position' },
      { text: 'Fecha de Ingreso', value: 'employmentInfo.entryDate' },
      { text: 'Período de Prueba', value: 'probationStatus' },
      { text: 'Tipo de Contrato', value: 'employmentInfo.contractType' },
      { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
    ],
    contractTypes: [
      { text: 'Tiempo Completo', value: 'fullTime' },
      { text: 'Medio Tiempo', value: 'partTime' },
      { text: 'Eventual', value: 'temporary' }
    ],
    accountTypes: [
      'Caja de Ahorro',
      'Cuenta Corriente',
      'Cuenta Sueldo'
    ],
    roles: []
  }),

  computed: {
    ...mapState({
      employeeFiles: state => state.employeeFiles.list,
      users: state => state.users.list
    }),
    
    ...mapGetters({
      getProbationStatus: 'employeeFiles/getProbationStatus',
      getSeniority: 'employeeFiles/getSeniority'
    }),
    
    employees() {
      // Combinar datos de legajos con datos de usuarios y también incluir usuarios sin legajo
      // Primero obtenemos los usuarios con legajo
      const usersWithFiles = this.employeeFiles.map(file => {
        const user = this.users.find(u => u._id === file.userId._id)
        return {
          ...file,
          userName: user ? user.name : file.userId.name,
          hasFile: true
        }
      })

      // Luego los usuarios sin legajo (solo los que tienen isEmployee = true)
      const employeesWithoutFiles = this.users
        .filter(user => 
          user.isEmployee === true &&
          !this.employeeFiles.some(file => file.userId._id === user._id)
        )
        .map(user => ({
          _id: null,
          userId: user,
          userName: user.name,
          employmentInfo: {
            position: 'Sin asignar',
            entryDate: null,
            contractType: null
          },
          hasFile: false
        }))
      
      // Combinamos ambos arrays
      return [...usersWithFiles, ...employeesWithoutFiles]
    },
    
    employeeList() {
      // Filtrar solo usuarios con isEmployee = true
      return this.users.filter(user => user.isEmployee === true)
    },
    
    formattedBirthDate: {
      get() {
        return this.birthDate 
          ? format(new Date(this.birthDate), 'dd/MM/yyyy') 
          : ''
      },
      set(val) {
        // No se usa, solo es para mantener el v-model
      }
    },

    formattedEntryDate: {
      get() {
        return this.entryDate 
          ? format(new Date(this.entryDate), 'dd/MM/yyyy') 
          : ''
      },
      set(val) {
        // No se usa, solo es para mantener el v-model
      }
    }
  },

  methods: {
    formatDate(date) {
      if (!date) return '-'
      return format(new Date(date), 'dd/MM/yyyy', { locale: es })
    },

    getProbationStatusColor(file) {
      if (!file.employmentInfo || !file.employmentInfo.entryDate) return 'grey'

      const status = this.getProbationStatus(file.employmentInfo.entryDate)
      return status.inProbation ? 'warning' : 'success'
    },
    
    getProbationStatusText(file) {
      if (!file.employmentInfo || !file.employmentInfo.entryDate) return 'Sin datos'

      const status = this.getProbationStatus(file.employmentInfo.entryDate)
      if (status.inProbation) {
        return `En prueba (${status.daysLeft} días restantes)`
      } else {
        return 'Confirmado'
      }
    },
    
    getContractTypeLabel(type) {
      const labelMap = {
        fullTime: 'Tiempo Completo',
        partTime: 'Medio Tiempo',
        temporary: 'Eventual'
      }
      return labelMap[type] || '-'
    },

    viewEmployeeFile(file) {
      // Si el usuario no tiene legajo, mostrar diálogo para crearlo
      if (!file.hasFile) {
        this.createOrEditFile({userId: file.userId})
        return
      }
      
      this.$router.push({ name: 'EmployeeFileDetail', params: { id: file._id } })
    },
    
    viewAttendance(userId) {
      // Primero necesitamos encontrar el legajo correspondiente a este usuario
      const employeeFile = this.employeeFiles.find(file => file.userId._id === userId);
      
      if (employeeFile) {
        // Redirigir al detalle del legajo con parámetro para activar la pestaña de asistencia
        this.$router.push({ 
          name: 'EmployeeFileDetail', 
          params: { id: employeeFile._id },
          query: { tab: 'attendance' }
        });
      } else {
        // Si el usuario no tiene legajo, informar al usuario
        this.$toast.warning('El empleado no tiene un legajo creado');
      }
    },
    
    viewSchedule(userId) {
      // Primero necesitamos encontrar el legajo correspondiente a este usuario
      const employeeFile = this.employeeFiles.find(file => file.userId._id === userId);
      
      if (employeeFile) {
        // Redirigir al detalle del legajo con parámetro para activar la pestaña de horarios
        this.$router.push({ 
          name: 'EmployeeFileDetail', 
          params: { id: employeeFile._id },
          query: { tab: 'schedule' }
        });
      } else {
        // Si el usuario no tiene legajo, informar al usuario
        this.$toast.warning('El empleado no tiene un legajo creado');
      }
    },
    
    createNewFile() {
      this.editedFile = {
        userId: null,
        personalInfo: {
          dni: '',
          cuil: '',
          birthDate: null,
          address: {
            street: '',
            number: '',
            floor: '',
            apartment: '',
            city: '',
            postalCode: '',
            province: ''
          },
          phone: '',
          alternativePhone: '',
          emergencyContact: {
            name: '',
            relationship: '',
            phone: ''
          }
        },
        bankInfo: {
          accountType: '',
          bank: '',
          cbu: '',
          alias: ''
        },
        employmentInfo: {
          entryDate: null,
          contractType: 'fullTime',
          roleId: null,
          position: '',
          hourlyRate: null,
          baseSalary: null,
          additionalInfo: ''
        }
      }
      this.birthDate = null
      this.entryDate = null
      this.fileDialog = true
    },
    
    createOrEditFile(file) {
      if (file) {
        // Si el archivo ya existe, editarlo
        if (file._id) {
          this.editedFile = JSON.parse(JSON.stringify(file))
          this.birthDate = file.personalInfo && file.personalInfo.birthDate
            ? new Date(file.personalInfo.birthDate).toISOString().substr(0, 10)
            : null
          this.entryDate = file.employmentInfo && file.employmentInfo.entryDate
            ? new Date(file.employmentInfo.entryDate).toISOString().substr(0, 10)
            : null
        } 
        // Si es un usuario sin legajo
        else if (file.userId) {
          this.createNewFile()
          // Asignar el usuario al legajo
          this.editedFile.userId = file.userId
          // Precargar el rol del usuario si existe
          if (file.userId.role && file.userId.role._id) {
            this.editedFile.employmentInfo.roleId = file.userId.role._id
          }
          // Precargar también la posición con el nombre del rol si existe
          if (file.userId.role && file.userId.role.name) {
            this.editedFile.employmentInfo.position = file.userId.role.name.charAt(0).toUpperCase() + file.userId.role.name.slice(1)
          }
        }
      } else {
        this.createNewFile()
      }
      
      this.fileDialog = true
    },
    
    async saveFile() {
      if (!this.$refs.fileForm.validate()) return
      
      this.loading = true
      
      try {
        // Actualizar campos de fechas
        if (this.birthDate) {
          this.editedFile.personalInfo.birthDate = new Date(this.birthDate)
        }
        
        if (this.entryDate) {
          this.editedFile.employmentInfo.entryDate = new Date(this.entryDate)
        }
        
        if (this.editedFile._id) {
          // Actualizar legajo existente
          await this.$store.dispatch('employeeFiles/update', this.editedFile)
          this.$toast.success('Legajo actualizado correctamente')
        } else {
          // Asignar el ID del empleado seleccionado
          if (this.editedFile.userId && this.editedFile.userId._id) {
            this.editedFile.userId = this.editedFile.userId._id
          }
          
          // Crear nuevo legajo
          await this.$store.dispatch('employeeFiles/create', this.editedFile)
          this.$toast.success('Legajo creado correctamente')
        }
        
        this.fileDialog = false
      } catch (error) {
        this.$toast.error(`Error al guardar el legajo: ${error.message}`)
      } finally {
        this.loading = false
      }
    },
    
    async exportToExcel() {
      this.loading = true
      
      try {
        // Aquí iría la implementación para exportar a Excel
        // Podría usar una librería como xlsx o hacer la solicitud al servidor
        
        this.$toast.success('Datos exportados exitosamente')
      } catch (error) {
        this.$toast.error(`Error al exportar datos: ${error.message}`)
      } finally {
        this.loading = false
      }
    }
  },
  
  async created() {
    this.loading = true
    
    try {
      await Promise.all([
        this.$store.dispatch('employeeFiles/fetchAll'),
        this.$store.dispatch('users/fetch'),
        this.$store.dispatch('roles/fetch')
      ])
      this.roles = this.$store.state.roles.list
      
      // Verificar si hay un parámetro de edición en la URL
      if (this.$route.query.edit) {
        // Buscar el legajo por ID
        const fileId = this.$route.query.edit
        const fileToEdit = this.employeeFiles.find(file => file._id === fileId)
        
        if (fileToEdit) {
          // Abrir el diálogo de edición con los datos del legajo
          this.createOrEditFile(fileToEdit)
        } else {
          this.$toast.error('No se encontró el legajo solicitado')
        }
      }
    } catch (error) {
      this.$toast.error(`Error al cargar datos: ${error.message}`)
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.v-data-table >>> th {
  font-weight: bold !important;
}
</style>
